/**
 * The bot-protection widget for the public auth forms.
 *
 * The challenge is a signed token plus an SVG the API inlines as a `data:` URL,
 * so there is no third-party script on the sign-in page, no image request, and
 * nothing to load before the form is usable.
 *
 * Two behaviours are load-bearing and easy to lose in a refactor:
 *
 * 1. **Reload on every exit path.** The server burns the token on the *first*
 *    attempt, right or wrong. A form that leaves the solved image on screen
 *    after a 400 shows the viewer a challenge that can no longer be solved, and
 *    they will type it correctly, fail again, and blame themselves. `reload()`
 *    belongs in the `finally` of every submit.
 * 2. **The honeypot stays in the DOM and out of the accessibility tree.**
 *    `display: none` is what naive scrapers skip; an off-screen field is what
 *    they fill. It is `aria-hidden` and `tabindex="-1"` so no person and no
 *    screen reader ever reaches it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/api/client";
import type { CaptchaChallenge, CaptchaFields, CaptchaForm } from "@/api/types";

export interface CaptchaState {
  /** False when the deployment has this form's captcha switched off. */
  enabled: boolean;
  loading: boolean;
  /** Set when the challenge itself could not be fetched, not when it was wrong. */
  error: string | null;
  imageDataUrl: string;
  answer: string;
  setAnswer: (value: string) => void;
  website: string;
  setWebsite: (value: string) => void;
  reload: () => Promise<void>;
  /** The three fields to merge into the submitted body. */
  fields: () => CaptchaFields;
}

const EMPTY: CaptchaChallenge = { enabled: true };

export function useCaptcha(form: CaptchaForm): CaptchaState {
  const [challenge, setChallenge] = useState<CaptchaChallenge>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState("");
  const [website, setWebsite] = useState("");
  // A successful submit navigates away while `reload()` is still in flight;
  // the ref keeps that from writing to an unmounted component.
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await api.anonymousGet<CaptchaChallenge>(`/api/v1/auth/captcha/${form}`);
      if (!mounted.current) return;
      setChallenge(next);
      setAnswer("");
    } catch {
      if (!mounted.current) return;
      // Fail visible, not silent: if we cannot fetch a challenge the submit
      // will be rejected server-side, and the viewer deserves to know why.
      setChallenge(EMPTY);
      setError("The security check could not be loaded. Try again.");
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const enabled = challenge.enabled !== false;
  return {
    enabled,
    loading,
    error,
    imageDataUrl: challenge.image_data_url ?? "",
    answer,
    setAnswer,
    website,
    setWebsite,
    reload,
    fields: () => ({
      website,
      captcha_token: challenge.token ?? "",
      captcha_answer: answer,
    }),
  };
}

/**
 * The honeypot. Rendered even when the captcha is disabled - it costs nothing
 * and it is the cheapest of the seven layers.
 */
export function Honeypot({ state }: { state: CaptchaState }) {
  return (
    <div aria-hidden="true" className="honeypot">
      <label htmlFor="website">Website</label>
      <input
        id="website"
        name="website"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={state.website}
        onChange={(event) => state.setWebsite(event.target.value)}
      />
    </div>
  );
}

export function CaptchaField({ state }: { state: CaptchaState }) {
  if (!state.enabled) return <Honeypot state={state} />;

  return (
    <>
      <Honeypot state={state} />
      <div className="field captcha">
        <label htmlFor="captcha_answer">Security check</label>
        <div className="captcha__challenge">
          {state.imageDataUrl ? (
            <img
              className="captcha__image"
              id="captcha-image"
              src={state.imageDataUrl}
              alt="Six characters to type into the field below"
              width={220}
              height={72}
            />
          ) : (
            <div className="captcha__image captcha__image--placeholder" role="presentation" />
          )}
          <button
            type="button"
            className="btn btn--quiet captcha__refresh"
            onClick={() => void state.reload()}
            disabled={state.loading}
            // The accessible name deliberately avoids the words "Security
            // check": that is the input's label, and a button whose name
            // contains another control's label is ambiguous to anyone - a
            // screen reader user included - navigating by name.
            aria-label="Show a different image"
          >
            {state.loading ? "Loading…" : "New image"}
          </button>
        </div>
        <input
          id="captcha_answer"
          name="captcha_answer"
          type="text"
          required
          value={state.answer}
          onChange={(event) => state.setAnswer(event.target.value)}
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
          maxLength={12}
          placeholder="Type the characters"
        />
        {state.error ? (
          <span className="captcha__hint captcha__hint--error">{state.error}</span>
        ) : (
          <span className="captcha__hint">
            Not case sensitive. Each image is good for one attempt.
          </span>
        )}
      </div>
    </>
  );
}
